"use client"

import { useState, useEffect, useRef } from "react"
import { X, ExternalLink } from "lucide-react"
import { usePathname } from "next/navigation"
import { useTranslation } from "@/lib/useTranslation"
import { trackPromoBannerClicked, trackPromoBannerDismissed } from "@/lib/posthog-events"

const HIDDEN_EXACT_PATHS = new Set([
  "/fixing",
  "/fixing-moscow",
  "/price",
  "/moscow/price",
  "/bullion",
  "/dashboard",
  "/sign-in",
  "/sign-up",
  "/bank-transfer",
])
const HIDDEN_PATH_PREFIXES = ["/bullion/", "/dashboard/", "/auth/"]

function isHiddenPath(pathname: string): boolean {
  if (HIDDEN_EXACT_PATHS.has(pathname)) return true
  return HIDDEN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

// Master on/off switch for the promo banner. Set to false to hide it everywhere.
const PROMO_ENABLED = false

const IS_RUSSIA_BUILD = process.env.NEXT_PUBLIC_REGION === "russia"

// Bump this to re-show the banner to users who previously dismissed it.
const PROMO_CAMPAIGN_ID = "bank_transfer_2026_q2"

const GOLD_COLOR = "#D4B27B"

// Banner copy split into three parts so the "gold" word can be color-highlighted.
// Brand name "Goldcenter" stays in latin letters across all languages.
const PROMO_PARTS: Record<"en" | "ru" | "hy", { before: string; gold: string; after: string }> = {
  en: { before: "Bank transfer ", gold: "gold", after: " at Goldcenter" },
  ru: { before: "", gold: "Золото", after: " по переводу на Goldcenter" },
  hy: { before: "Փոխանցումով ", gold: "ոսկի", after: " Goldcenter-ում" },
}

// Whether this campaign should run on the moscowgoldcenter.ru build.
// The ratecenter.am promo is Yerevan-only; flip to true for future Russia-targeted campaigns.
const SHOW_ON_RUSSIA_BUILD = false

// Delay before the banner slides in — avoids the "kill it before I read it" reflex.
const APPEAR_DELAY_MS = 1500
const TRANSITION_MS = 400

export function PromoBanner() {
  // CLAUDE.md §7a: NEVER read document/dataset/cookies in a lazy useState
  // initializer — SSR returns false (no document), CSR returns true for
  // dismissed users, structural HTML #418 mismatch fires on every page-load.
  // Instead, both server and first client render return `false`, then a
  // post-mount useEffect reads the dataset and updates state if needed.
  // The user-visible artifact is harmless: `visible=false` is the initial
  // state, so the banner is collapsed to 0fr-grid (invisible) until the
  // APPEAR_DELAY_MS timer fires — the effect runs and hides it before then.
  const [dismissed, setDismissed] = useState(false)
  const [visible, setVisible] = useState(false)
  const [unmounted, setUnmounted] = useState(false)
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { language } = useTranslation()
  const pathname = usePathname()

  const hiddenByPath = isHiddenPath(pathname)

  // Read the dismissed flag from the head-script-populated dataset AFTER
  // mount so server + first client render agree on `dismissed=false`.
  useEffect(() => {
    if (typeof document === "undefined") return
    if (document.documentElement.dataset.promoDismissed === PROMO_CAMPAIGN_ID) {
      setDismissed(true)
    }
  }, [])

  useEffect(() => {
    if (dismissed || hiddenByPath) return
    const id = setTimeout(() => setVisible(true), APPEAR_DELAY_MS)
    return () => clearTimeout(id)
  }, [dismissed, hiddenByPath])

  useEffect(() => {
    return () => {
      if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current)
    }
  }, [])

  if (!PROMO_ENABLED) return null
  if (IS_RUSSIA_BUILD && !SHOW_ON_RUSSIA_BUILD) return null
  if (dismissed || unmounted) return null

  const shouldShow = visible && !hiddenByPath

  const parts = PROMO_PARTS[language] ?? PROMO_PARTS.en

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    trackPromoBannerDismissed(pathname)
    document.cookie = `gc_promo_dismissed=${PROMO_CAMPAIGN_ID}; path=/; max-age=31536000; SameSite=Lax`
    document.documentElement.dataset.promoDismissed = PROMO_CAMPAIGN_ID
    setVisible(false)
    unmountTimerRef.current = setTimeout(() => setUnmounted(true), TRANSITION_MS)
  }

  return (
    <div
      suppressHydrationWarning
      className="grid"
      style={{
        gridTemplateRows: shouldShow ? "1fr" : "0fr",
        transition: `grid-template-rows ${TRANSITION_MS}ms ease-out`,
      }}
    >
      <div
        className="overflow-hidden"
        style={{
          opacity: shouldShow ? 1 : 0,
          transition: `opacity ${TRANSITION_MS}ms ease-out`,
        }}
      >
        <div
          className={`bg-emerald-600 text-white text-center relative whitespace-nowrap ${
            language === "hy"
              ? "text-[9px] xs:text-xs sm:text-sm"
              : "text-[10px] xs:text-xs sm:text-sm"
          }`}
        >
          <a
            href="/bank-transfer"
            className="inline-flex items-center gap-1 py-2 px-6 xs:px-7 sm:px-8 hover:bg-emerald-700 transition-colors"
            onClick={() => trackPromoBannerClicked(pathname)}
          >
            <span>
              {parts.before}
              <span style={{ color: GOLD_COLOR }} className="font-semibold">{parts.gold}</span>
              {parts.after}
            </span>
            <ExternalLink className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
          </a>
          <button
            onClick={handleDismiss}
            className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-emerald-700 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-2.5 h-2.5 xs:w-3 xs:h-3 sm:w-3.5 sm:h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}
